import { useEffect, useRef, useState, useMemo } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import { ChevronRightIcon, ChevronDownIcon, PlusIcon, MinusIcon, AlertIcon } from './Icons.jsx';

// Use the public CDN copy of the worker — avoids MIME type / .mjs serving
// issues on hosts whose Nginx config doesn't know about .mjs.
pdfjsLib.GlobalWorkerOptions.workerSrc =
  `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

/**
 * Custom PDF viewer built on PDF.js.
 * Renders all pages to canvas, with branded controls (no browser chrome).
 *
 * Props:
 *   url           — PDF URL
 *   primaryColor  — accent color (defaults to var(--primary))
 *   maxHeight     — optional viewport height for the scroll container
 */
export default function PdfViewer({ url, primaryColor = '#7C3AED', maxHeight }) {
  const containerRef = useRef(null);
  const pageRefs = useRef([]);
  const [pdf, setPdf] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.0); // Multiplier on top of fit-width base scale
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Load the PDF document
  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    setLoading(true);
    setError('');

    pdfjsLib.getDocument({ url, withCredentials: false }).promise
      .then(doc => {
        if (cancelled) return;
        setPdf(doc);
        setNumPages(doc.numPages);
        setCurrentPage(1);
        pageRefs.current = new Array(doc.numPages);
      })
      .catch(err => {
        if (cancelled) return;
        console.error('PDF load failed:', err);
        setError(err.message || 'Failed to load PDF');
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [url]);

  // Render all pages whenever PDF or scale changes
  useEffect(() => {
    if (!pdf || !containerRef.current) return;
    let cancelled = false;
    const containerWidth = containerRef.current.clientWidth - 24; // padding allowance

    (async () => {
      for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
        if (cancelled) return;
        const page = await pdf.getPage(pageNumber);
        const baseViewport = page.getViewport({ scale: 1 });
        // Fit-width base scale, then user zoom multiplier
        const fitScale = Math.max(0.4, containerWidth / baseViewport.width);
        const viewport = page.getViewport({ scale: fitScale * scale });

        const canvas = pageRefs.current[pageNumber - 1];
        if (!canvas) continue;

        // Use device pixel ratio for sharper rendering on retina/mobile
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        canvas.width = Math.floor(viewport.width * dpr);
        canvas.height = Math.floor(viewport.height * dpr);
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;

        const ctx = canvas.getContext('2d');
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        await page.render({
          canvasContext: ctx,
          viewport,
          transform: dpr !== 1 ? [dpr, 0, 0, dpr, 0, 0] : null,
        }).promise;

        page.cleanup();
      }
    })();

    return () => { cancelled = true; };
  }, [pdf, scale]);

  // Track which page is currently centered in the viewport for the page indicator
  useEffect(() => {
    if (!pdf || !containerRef.current) return;
    const container = containerRef.current;
    const handleScroll = () => {
      const containerRect = container.getBoundingClientRect();
      const containerCenter = containerRect.top + containerRect.height / 2;
      let bestPage = 1;
      let bestDistance = Infinity;
      pageRefs.current.forEach((canvas, idx) => {
        if (!canvas) return;
        const r = canvas.getBoundingClientRect();
        const center = r.top + r.height / 2;
        const distance = Math.abs(center - containerCenter);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestPage = idx + 1;
        }
      });
      setCurrentPage(bestPage);
    };
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [pdf]);

  const goToPage = (pageNumber) => {
    const target = pageRefs.current[pageNumber - 1];
    if (target && containerRef.current) {
      const offsetTop = target.offsetTop - 8;
      containerRef.current.scrollTo({ top: offsetTop, behavior: 'smooth' });
    }
  };

  const handleZoomIn = () => setScale(s => Math.min(3, s + 0.25));
  const handleZoomOut = () => setScale(s => Math.max(0.5, s - 0.25));
  const handleResetZoom = () => setScale(1);

  const containerStyle = useMemo(() => ({
    background: 'var(--bg, #FAFAFB)',
    borderRadius: '14px',
    border: `1px solid ${primaryColor}20`,
    overflow: 'hidden',
    position: 'relative',
  }), [primaryColor]);

  return (
    <div style={containerStyle}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: '8px',
        padding: '10px 14px',
        background: 'white',
        borderBottom: '1px solid #F1F5F9',
      }}>
        {/* Page navigator */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <button
            type="button"
            aria-label="Previous page"
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage <= 1 || loading}
            style={iconBtnStyle(primaryColor, currentPage <= 1 || loading)}
          >
            <span style={{ transform: 'rotate(180deg)', display: 'inline-flex' }}>
              <ChevronRightIcon size={14} />
            </span>
          </button>
          <div style={{
            fontSize: '12px', fontWeight: 600,
            padding: '0 10px', color: 'var(--text-2)',
            minWidth: '64px', textAlign: 'center',
          }}>
            {numPages > 0 ? `${currentPage} / ${numPages}` : '— / —'}
          </div>
          <button
            type="button"
            aria-label="Next page"
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage >= numPages || loading}
            style={iconBtnStyle(primaryColor, currentPage >= numPages || loading)}
          >
            <ChevronRightIcon size={14} />
          </button>
        </div>

        {/* Zoom controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <button
            type="button"
            aria-label="Zoom out"
            onClick={handleZoomOut}
            disabled={scale <= 0.5 || loading}
            style={iconBtnStyle(primaryColor, scale <= 0.5 || loading)}
          >
            <MinusIcon size={14} />
          </button>
          <button
            type="button"
            onClick={handleResetZoom}
            disabled={loading}
            style={{
              fontSize: '11px', fontWeight: 700,
              padding: '6px 10px', borderRadius: '8px',
              border: 'none', cursor: loading ? 'default' : 'pointer',
              background: 'transparent', color: 'var(--text-2)',
              fontFamily: 'inherit',
              minWidth: '46px',
            }}
          >
            {Math.round(scale * 100)}%
          </button>
          <button
            type="button"
            aria-label="Zoom in"
            onClick={handleZoomIn}
            disabled={scale >= 3 || loading}
            style={iconBtnStyle(primaryColor, scale >= 3 || loading)}
          >
            <PlusIcon size={14} />
          </button>
        </div>
      </div>

      {/* Pages */}
      <div
        ref={containerRef}
        style={{
          maxHeight: maxHeight || 'calc(100vh - 240px)',
          minHeight: '420px',
          overflow: 'auto',                 // both axes scroll when zoomed
          WebkitOverflowScrolling: 'touch', // smooth scroll on iOS
          touchAction: 'pan-x pan-y pinch-zoom',
          padding: '12px',
          display: 'flex', flexDirection: 'column',
          alignItems: scale > 1 ? 'flex-start' : 'center',
          gap: '12px',
        }}
      >
        {loading && (
          <div style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px',
            padding: '60px 20px', color: 'var(--text-2)',
          }}>
            <div style={{
              width: '40px', height: '40px',
              border: '3px solid var(--border)',
              borderTopColor: primaryColor,
              borderRadius: '50%',
              animation: 'almenu-pdf-spin 0.8s linear infinite',
            }} />
            <div style={{ fontSize: '12px' }}>Loading PDF...</div>
          </div>
        )}

        {error && (
          <div style={{
            padding: '20px',
            background: '#FEE2E2', color: '#991B1B',
            borderRadius: '12px',
            display: 'flex', alignItems: 'center', gap: '10px',
            fontSize: '13px',
            maxWidth: '420px',
          }}>
            <AlertIcon size={18} /> Couldn't load PDF: {error}
          </div>
        )}

        {!loading && !error && Array.from({ length: numPages }, (_, idx) => (
          <canvas
            key={idx}
            ref={el => { pageRefs.current[idx] = el; }}
            style={{
              boxShadow: '0 1px 3px rgba(15,23,42,0.08), 0 4px 12px rgba(15,23,42,0.05)',
              borderRadius: '6px',
              background: 'white',
              display: 'block',
              flexShrink: 0, // don't shrink inside the flex column
            }}
          />
        ))}
      </div>

      <style>{`
        @keyframes almenu-pdf-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

function iconBtnStyle(primaryColor, disabled) {
  return {
    width: '30px', height: '30px',
    border: 'none',
    background: disabled ? 'transparent' : `${primaryColor}10`,
    color: disabled ? 'var(--text-3)' : primaryColor,
    borderRadius: '8px',
    cursor: disabled ? 'default' : 'pointer',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    transition: 'background 0.15s',
    fontFamily: 'inherit',
  };
}
